import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { useAuth } from '@/hooks';
import { useWebAccess } from '@/providers/WebAccessProvider';
import { createLogger } from '@/lib/structuredLogger';

const logger = createLogger('ChatContext');

export type Message = {
  id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at?: string;
  status?: string | null;
  idempotency_key?: string | null;
  metadata?: unknown;
};

export type ChatSession = {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
  is_pinned: boolean;
};

interface ChatContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggleChat: () => void;
  // Session management
  activeSession: ChatSession | null;
  sessions: ChatSession[];
  messages: Message[];
  isLoadingSession: boolean;
  // Actions
  loadSessions: () => Promise<void>;
  loadSession: (sessionId: string) => Promise<void>;
  isCurrentSession: (sessionId: string) => boolean;
  createSession: (signal?: AbortSignal) => Promise<string | null>;
  deleteSession: (sessionId: string) => Promise<void>;
  togglePin: (sessionId: string) => Promise<void>;
  clearCurrentSession: () => void;
  // History drawer
  isHistoryOpen: boolean;
  setIsHistoryOpen: (open: boolean) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const STORAGE_KEY = 'vitalworkflow_chat_session';

type ChatSessionRow = Tables<'chat_sessions'>;
type ChatMessageRow = Tables<'chat_messages'>;

function normalizeChatTimestamp(value: string | null | undefined): string {
  return value ?? new Date(0).toISOString();
}

function normalizeChatSession(row: ChatSessionRow): ChatSession {
  return {
    id: row.id,
    title: row.title,
    created_at: normalizeChatTimestamp(row.created_at),
    updated_at: normalizeChatTimestamp(row.updated_at),
    is_archived: row.is_archived === true,
    is_pinned: row.is_pinned === true,
  };
}

function normalizeMessageRole(role: string): Message['role'] {
  return role === 'user' || role === 'assistant' || role === 'system' ? role : 'assistant';
}

function normalizeChatMessage(row: ChatMessageRow): Message {
  return {
    id: row.id,
    role: normalizeMessageRole(row.role),
    content: row.content,
    created_at: normalizeChatTimestamp(row.created_at ?? row.updated_at),
    status: row.status,
    idempotency_key: row.idempotency_key,
    metadata: row.metadata,
  };
}

function getMetadataTurnKey(message: Message): string | null {
  const metadata = message.metadata;
  if (!metadata || typeof metadata !== 'object') return null;

  const turnKey = (metadata as Record<string, unknown>).turn_key;
  return typeof turnKey === 'string' && turnKey.length > 0 ? turnKey : null;
}

function getOptimisticTurnKey(message: Message): string | null {
  if (!message.id) return null;

  if (message.role === 'assistant' && message.id.startsWith('assistant:')) {
    return message.id.slice('assistant:'.length);
  }

  if (message.role === 'user' && !message.id.startsWith('assistant:')) {
    return message.id;
  }

  return null;
}

function getMessageMergeKeys(message: Message): string[] {
  const keys = new Set<string>();

  if (message.id) keys.add(`id:${message.id}`);
  if (message.idempotency_key) keys.add(`idempotency:${message.idempotency_key}`);

  const metadataTurnKey = getMetadataTurnKey(message);
  const optimisticTurnKey = getOptimisticTurnKey(message);
  const turnKey = metadataTurnKey ?? optimisticTurnKey;
  if (turnKey) keys.add(`turn:${turnKey}:${message.role}`);

  return [...keys];
}

function shouldKeepLocalAssistantContent(local: Message, incoming: Message): boolean {
  if (local.role !== 'assistant' || incoming.role !== 'assistant') return false;
  if (!local.content.trim()) return false;

  const incomingContent = incoming.content.trim();
  if (!incomingContent) return true;

  return incoming.status !== 'completed' && local.content.startsWith(incoming.content);
}

function mergeMessage(local: Message, incoming: Message): Message {
  if (
    local.role === 'assistant' &&
    (local.status === 'completed' || local.status === 'failed') &&
    (incoming.status === 'pending' || incoming.status === 'streaming')
  ) {
    return { ...incoming, ...local, id: incoming.id ?? local.id };
  }
  if (shouldKeepLocalAssistantContent(local, incoming)) {
    return {
      ...local,
      ...incoming,
      content: local.content,
    };
  }

  return {
    ...local,
    ...incoming,
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export function mergeChatMessages(
  localMessages: Message[],
  incomingMessages: Message[],
): Message[] {
  if (localMessages.length === 0) return incomingMessages;
  if (incomingMessages.length === 0) return localMessages;

  const merged = [...localMessages];
  const keyToIndex = new Map<string, number>();

  const registerKeys = (message: Message, index: number) => {
    for (const key of getMessageMergeKeys(message)) {
      keyToIndex.set(key, index);
    }
  };

  merged.forEach(registerKeys);

  for (const incoming of incomingMessages) {
    const existingIndex = getMessageMergeKeys(incoming)
      .map((key) => keyToIndex.get(key))
      .find((index): index is number => typeof index === 'number');

    if (existingIndex === undefined) {
      merged.push(incoming);
      registerKeys(incoming, merged.length - 1);
      continue;
    }

    merged[existingIndex] = mergeMessage(merged[existingIndex], incoming);
    registerKeys(merged[existingIndex], existingIndex);
  }

  return merged;
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { healthReady, readinessGeneration } = useWebAccess();
  const [isOpen, setChatOpen] = useState(false);
  const [isHistoryOpen, setHistoryOpen] = useState(false);
  const [activeSession, setActiveSession] = useState<ChatSession | null>(null);
  const activeSessionRef = useRef(activeSession);
  activeSessionRef.current = activeSession;
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const selectionVersionRef = useRef(0);
  const selectionTargetRef = useRef<string | null>(null);
  const sessionsVersionRef = useRef(0);
  const selectionAbortRef = useRef<AbortController | null>(null);
  const sessionsAbortRef = useRef<AbortController | null>(null);
  const accessRef = useRef({ owner: userId, healthReady, readinessGeneration, epoch: 0 });
  if (
    accessRef.current.owner !== userId ||
    accessRef.current.healthReady !== healthReady ||
    accessRef.current.readinessGeneration !== readinessGeneration
  ) {
    accessRef.current = {
      owner: userId,
      healthReady,
      readinessGeneration,
      epoch: accessRef.current.epoch + 1,
    };
    selectionVersionRef.current += 1;
    sessionsVersionRef.current += 1;
    selectionTargetRef.current = null;
  }
  const accessEpoch = accessRef.current.epoch;
  const [stateEpoch, setStateEpoch] = useState(accessEpoch);
  const previousOwnerRef = useRef(user?.id ?? null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      selectionVersionRef.current += 1;
      sessionsVersionRef.current += 1;
      selectionAbortRef.current?.abort();
      sessionsAbortRef.current?.abort();
    };
  }, []);

  const canAccess = useCallback(
    (owner: string | null, epoch: number) =>
      mountedRef.current &&
      owner !== null &&
      accessRef.current.owner === owner &&
      accessRef.current.healthReady &&
      accessRef.current.epoch === epoch,
    [],
  );
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (canAccess(userId, accessRef.current.epoch)) setChatOpen(open);
    },
    [userId, canAccess],
  );
  const setIsHistoryOpen = useCallback(
    (open: boolean) => {
      if (canAccess(userId, accessRef.current.epoch)) setHistoryOpen(open);
    },
    [userId, canAccess],
  );
  const toggleChat = useCallback(() => {
    if (canAccess(userId, accessRef.current.epoch)) setChatOpen((prev) => !prev);
  }, [userId, canAccess]);
  const isCurrentSession = useCallback(
    (sessionId: string) =>
      canAccess(userId, accessEpoch) &&
      previousOwnerRef.current === userId &&
      selectionTargetRef.current === sessionId,
    [userId, accessEpoch, canAccess],
  );

  useEffect(() => {
    activeSessionRef.current = null;
    setActiveSession(null);
    setMessages([]);
    setSessions([]);
    setIsLoadingSession(false);
    setChatOpen(false);
    setHistoryOpen(false);
    setStateEpoch(accessEpoch);
    return () => {
      selectionAbortRef.current?.abort();
      sessionsAbortRef.current?.abort();
    };
  }, [accessEpoch]);

  // Load all sessions for history
  const loadSessions = useCallback(async () => {
    const owner = userId;
    const epoch = accessRef.current.epoch;
    if (!owner || !canAccess(owner, epoch)) return;
    const version = ++sessionsVersionRef.current;
    sessionsAbortRef.current?.abort();
    const controller = new AbortController();
    sessionsAbortRef.current = controller;

    try {
      const { data, error } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', owner)
        .eq('is_archived', false)
        .order('updated_at', { ascending: false })
        .limit(50)
        .abortSignal(controller.signal);

      if (!error && data && canAccess(owner, epoch) && sessionsVersionRef.current === version) {
        setSessions(data.map(normalizeChatSession));
      }
    } finally {
      if (sessionsAbortRef.current === controller) sessionsAbortRef.current = null;
    }
  }, [userId, canAccess]);

  // Load a specific session with messages
  const loadSession = useCallback(
    async (sessionId: string) => {
      const owner = userId;
      const epoch = accessRef.current.epoch;
      if (!owner || !canAccess(owner, epoch)) return;
      const version = ++selectionVersionRef.current;
      selectionTargetRef.current = sessionId;
      selectionAbortRef.current?.abort();
      const controller = new AbortController();
      selectionAbortRef.current = controller;
      let committed = false;
      const isCurrent = () => canAccess(owner, epoch) && selectionVersionRef.current === version;
      setIsLoadingSession(true);

      try {
        // Load session
        const { data: sessionData, error: sessionError } = await supabase
          .from('chat_sessions')
          .select('*')
          .eq('id', sessionId)
          .eq('user_id', owner)
          .abortSignal(controller.signal)
          .maybeSingle();

        if (!isCurrent()) return;

        if (sessionError) {
          if (!import.meta.env.PROD) logger.error('Failed to load session', sessionError);
          localStorage.removeItem(STORAGE_KEY); // Clear invalid session reference
          return;
        }

        if (!sessionData) {
          if (activeSessionRef.current?.id === sessionId) {
            activeSessionRef.current = null;
            setActiveSession(null);
            setMessages([]);
          }
          return;
        }

        // Load messages
        const { data: messagesData, error: messagesError } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('session_id', sessionId)
          .order('created_at', { ascending: true })
          .abortSignal(controller.signal);

        if (!isCurrent()) return;

        if (messagesError) {
          if (!import.meta.env.PROD) logger.error('Failed to load messages', messagesError);
          return;
        }

        setActiveSession(normalizeChatSession(sessionData));
        setMessages((messagesData || []).map(normalizeChatMessage));
        committed = true;

        // Store in localStorage for persistence
        localStorage.setItem(STORAGE_KEY, sessionId);
      } finally {
        if (isCurrent()) {
          if (!committed) {
            selectionTargetRef.current = activeSessionRef.current?.id ?? null;
            if (selectionTargetRef.current)
              localStorage.setItem(STORAGE_KEY, selectionTargetRef.current);
            else localStorage.removeItem(STORAGE_KEY);
          }
          setIsLoadingSession(false);
        }
        if (selectionAbortRef.current === controller) selectionAbortRef.current = null;
      }
    },
    [userId, canAccess],
  );

  // Create a new session
  const createSession = useCallback(
    async (signal?: AbortSignal): Promise<string | null> => {
      const owner = userId;
      const epoch = accessRef.current.epoch;
      if (!owner || !canAccess(owner, epoch) || signal?.aborted) return null;
      const version = ++selectionVersionRef.current;
      selectionTargetRef.current = null;
      selectionAbortRef.current?.abort();
      setIsLoadingSession(false);
      const isCurrent = () =>
        canAccess(owner, epoch) && selectionVersionRef.current === version && !signal?.aborted;
      const { data, error } = await supabase
        .from('chat_sessions')
        .insert({ user_id: owner })
        .select()
        .single();

      if (!isCurrent()) return null;

      if (error || !data) {
        if (!import.meta.env.PROD) logger.error('Failed to create session', error);
        return null;
      }

      setActiveSession(normalizeChatSession(data));
      selectionTargetRef.current = data.id;
      setMessages([]);
      localStorage.setItem(STORAGE_KEY, data.id);

      // Refresh sessions list
      loadSessions();

      return data.id;
    },
    [userId, canAccess, loadSessions],
  );

  // Delete a session
  const deleteSession = useCallback(
    async (sessionId: string) => {
      const owner = userId;
      const epoch = accessRef.current.epoch;
      if (!owner || !canAccess(owner, epoch)) return;

      const { error } = await supabase
        .from('chat_sessions')
        .delete()
        .eq('id', sessionId)
        .eq('user_id', owner);

      if (!error && canAccess(owner, epoch)) {
        const deletingSelection = selectionTargetRef.current === sessionId;
        if (deletingSelection) {
          selectionVersionRef.current += 1;
          selectionTargetRef.current = null;
          selectionAbortRef.current?.abort();
          setIsLoadingSession(false);
        }
        // A failed pending selection must not recover a committed chat that was deleted meanwhile.
        if (deletingSelection || activeSessionRef.current?.id === sessionId) {
          setActiveSession(null);
          activeSessionRef.current = null;
          setMessages([]);
          localStorage.removeItem(STORAGE_KEY);
        }
        // Refresh list
        loadSessions();
      }
    },
    [userId, canAccess, loadSessions],
  );

  // Toggle pin status of a session
  const togglePin = useCallback(
    async (sessionId: string) => {
      const owner = userId;
      const epoch = accessRef.current.epoch;
      if (!owner || !canAccess(owner, epoch)) return;

      const session = sessions.find((s) => s.id === sessionId);
      if (!session) return;

      const newPinned = !session.is_pinned;
      const { error } = await supabase
        .from('chat_sessions')
        .update({ is_pinned: newPinned })
        .eq('id', sessionId)
        .eq('user_id', owner);

      if (!error && canAccess(owner, epoch)) {
        setSessions((prev) =>
          canAccess(owner, epoch)
            ? prev.map((s) => (s.id === sessionId ? { ...s, is_pinned: newPinned } : s))
            : prev,
        );
        setActiveSession((prev) =>
          canAccess(owner, epoch) && prev?.id === sessionId
            ? { ...prev, is_pinned: newPinned }
            : prev,
        );
      }
    },
    [userId, canAccess, sessions],
  );

  // Clear current session (start fresh without deleting)
  const clearCurrentSession = useCallback(() => {
    if (!canAccess(userId, accessRef.current.epoch)) return;
    selectionVersionRef.current += 1;
    selectionTargetRef.current = null;
    activeSessionRef.current = null;
    selectionAbortRef.current?.abort();
    setIsLoadingSession(false);
    setActiveSession(null);
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
  }, [userId, canAccess]);

  // Load last session on mount / user change
  useEffect(() => {
    const owner = userId;
    if (previousOwnerRef.current !== owner) {
      if (previousOwnerRef.current) localStorage.removeItem(STORAGE_KEY);
      previousOwnerRef.current = owner;
    }
    if (!owner) return;

    const epoch = accessRef.current.epoch;
    if (!canAccess(owner, epoch)) return;

    const init = async () => {
      const version = selectionVersionRef.current;
      await loadSessions();
      if (!canAccess(owner, epoch) || selectionVersionRef.current !== version) return;

      // Try to restore last session from localStorage
      const lastSessionId = localStorage.getItem(STORAGE_KEY);
      if (lastSessionId) {
        await loadSession(lastSessionId);
      }
    };

    init();
  }, [userId, healthReady, readinessGeneration, canAccess, loadSessions, loadSession]);

  const stateBelongsToOwner =
    healthReady &&
    userId !== null &&
    previousOwnerRef.current === userId &&
    stateEpoch === accessEpoch;
  return (
    <ChatContext.Provider
      value={{
        isOpen: stateBelongsToOwner && isOpen,
        setIsOpen,
        toggleChat,
        activeSession: stateBelongsToOwner ? activeSession : null,
        sessions: stateBelongsToOwner ? sessions : [],
        messages: stateBelongsToOwner ? messages : [],
        isLoadingSession: stateBelongsToOwner && isLoadingSession,
        loadSessions,
        loadSession,
        isCurrentSession,
        createSession,
        deleteSession,
        togglePin,
        clearCurrentSession,
        isHistoryOpen: stateBelongsToOwner && isHistoryOpen,
        setIsHistoryOpen,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useChatContext() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
}
